import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { getUserMemberships, isStaff, useAuth } from "../lib/auth";
import {
  capturedAuthError,
  capturedRecoveryOrInvite,
  capturedTokenHashParams,
} from "../lib/authError.mjs";
import { resolveRoot } from "../lib/rootRoute.mjs";
import { getActiveWorkspace, getDefaultWorkspace } from "../lib/tenantContext";
import { resolveWorkspace } from "../lib/workspaceRoute.mjs";
import AuthError from "../pages/AuthError";
import ComingSoon from "../pages/ComingSoon";
import Skeleton from "./states/Skeleton";

/**
 * MER-515: the bare domain "/" used to render ComingSoon unconditionally,
 * so a live, signed-in customer (Seth / Best Disposal) who bookmarked the
 * root, or logged in without a deep link, dead-ended on the placeholder
 * even though /bestdisposal is live. Resolve where an authenticated user
 * belongs instead:
 *
 *   - auth-callback error in URL  -> AuthError (expired/invalid sign-in link)
 *   - staff                       -> /admin
 *   - one (or first) tenant slug  -> /<slug>
 *   - signed in, no membership    -> ComingSoon (genuinely unprovisioned)
 *   - signed out                  -> ComingSoon (anonymous holding page;
 *                                    it carries a Sign in link)
 *
 * Never navigates to "/" itself (that would loop): the no-home cases
 * render ComingSoon in place.
 */
export default function RootResolver() {
  // MER-515 part 3: Supabase redirects a failed email link to the root with
  // the error in the URL hash (Site URL = bare domain, per PLATFORM_STATE).
  // Captured at module load before the lazy Supabase client (detectSessionInUrl)
  // can clear it; decided before session/loading since the error is definitive.
  const [authError] = useState(capturedAuthError);
  // MER-519: a valid recovery / invite callback (type=recovery|invite in the
  // page-load hash) routes to /set-password so the user establishes a reusable
  // password, instead of resolving straight to the dashboard. Snapshotted, so
  // it's known synchronously before the session finishes exchanging. SetPassword
  // clears the snapshot on success, so the post-set return to "/" resolves
  // normally (no re-route loop).
  const [recoveryFlow] = useState(capturedRecoveryOrInvite);
  // MER-523: a prefetch-safe token_hash recovery/invite link (?token_hash=&type=)
  // may land on "/" when the invite template targets the Site URL. Snapshotted
  // at load like recoveryFlow.
  const [tokenHashFlow] = useState(capturedTokenHashParams);
  const { session, isLoading } = useAuth();
  const staff = isStaff(session);
  // Key the lookup on the stable user id, not the session OBJECT: a token
  // autorefresh emits a new session reference for the SAME user, and keying
  // on the object would re-fire the effect and flash the skeleton back.
  const userId = session?.user?.id ?? null;
  // MER-532: resolve ALL active memberships, then let resolveWorkspace decide.
  // undefined = still resolving; { slug } = a single home; { picker: true } =
  // >1 membership and no active/default, so route to the interstitial; null =
  // resolved to no membership (unprovisioned).
  const [home, setHome] = useState<{ slug: string } | { picker: true } | null | undefined>(
    undefined,
  );

  useEffect(() => {
    // Only a signed-in member needs the tenant_members lookup. Signed-out
    // and staff sessions are decided without it (and staff may legitimately
    // have no membership row, so they must not wait on this fetch).
    if (isLoading || !userId || staff) return;
    let cancelled = false;
    setHome(undefined);
    getUserMemberships()
      .then((memberships) => {
        if (cancelled) return;
        const decision = resolveWorkspace({
          slugs: memberships.map((m) => m.slug),
          activeSlug: getActiveWorkspace(userId),
          defaultSlug: getDefaultWorkspace(userId),
        });
        if (decision.kind === "home") setHome({ slug: decision.slug });
        else if (decision.kind === "picker") setHome({ picker: true });
        else setHome(null);
      })
      .catch(() => {
        if (!cancelled) setHome(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isLoading, userId, staff]);

  // Map the membership decision onto the resolveRoot contract: a single home is
  // a slug; the picker passes slug=null + needsWorkspaceChoice so resolveRoot
  // routes to /choose-workspace; still-resolving stays undefined (skeleton).
  const slug: string | null | undefined =
    home === undefined ? undefined : home && "slug" in home ? home.slug : null;
  const needsWorkspaceChoice = Boolean(home && "picker" in home);

  // MER-521: the full precedence lives in resolveRoot (pure, unit-tested over
  // the whole matrix). The comments on each branch there explain WHY the order
  // is load-bearing (e.g. recovery/token_hash routing must follow the isLoading
  // wait so detectSessionInUrl can finish the hash exchange; the auth-error is
  // gated on no-session). window.location.search is read here, at the redirect,
  // so the token_hash query is preserved verbatim.
  const decision = resolveRoot({
    isLoading,
    authError,
    recoveryFlow,
    tokenHashFlow,
    search: typeof window !== "undefined" ? window.location.search : "",
    hasSession: Boolean(session),
    staff,
    slug,
    needsWorkspaceChoice,
  });
  switch (decision.kind) {
    case "skeleton":
      return <RootSkeleton />;
    case "redirect":
      return <Navigate to={decision.to} replace />;
    case "authError":
      return <AuthError code={decision.code} />;
    case "comingSoon":
      return <ComingSoon showSignIn={decision.showSignIn} />;
    default: {
      // MER-521 review: exhaustiveness guard. Without it, adding a RootRouteDecision
      // kind (e.g. a future MER-523-style flow) and forgetting a case here would
      // silently render a blank root with a GREEN typecheck (the component's
      // inferred return widens to include undefined). The `never` assignment turns
      // that drift into a compile error instead.
      const _exhaustive: never = decision;
      return _exhaustive;
    }
  }
}

function RootSkeleton() {
  return (
    <main style={{ padding: 32, display: "grid", placeItems: "center", minHeight: "60vh" }}>
      <Skeleton label="Loading your dashboard" height={80} />
    </main>
  );
}
